import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { filter } from 'rxjs';

import { Country, FormStep } from '@features/succession/enums';
import { DeceasedInformationForm } from '@features/succession/forms';
import { CustomerInformation, DeceasedInformation } from '@features/succession/models';
import { SuccessionFacade } from '@features/succession/succession.facade';

@Injectable({ providedIn: 'root' })
export class DeceasedInformationFacade {
  protected destroyRef = inject(DestroyRef);

  public readonly successionFacade = inject(SuccessionFacade);

  public readonly formBuilder = inject(FormBuilder);

  public deceasedInformationForm: FormGroup<DeceasedInformationForm> =
    this.formBuilder.group<DeceasedInformationForm>({
      gender: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      name: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      birthname: new FormControl('', { nonNullable: true }),
      firstname: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      dateOfBirth: new FormControl(undefined, {
        validators: [Validators.required],
        nonNullable: true,
      }),
      countryOfBirth: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      departmentOfBirth: new FormControl('', { nonNullable: true }),
      communeOfBirth: new FormControl('', { nonNullable: true }),
      address: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      zipcode: new FormControl('', { nonNullable: true }),
      complementaryAddress: new FormControl('', { nonNullable: true }),
      city: new FormControl('', { nonNullable: true }),
      country: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      maritalStatus: new FormControl('', { validators: [Validators.required], nonNullable: true }),
      iban: new FormControl('', { nonNullable: true }),
    });

  constructor() {
    this.registerCountriesFieldValidators();
    this.setDeceasedFormDefaultValues();
  }

  public get gender(): FormControl<string> {
    return this.deceasedInformationForm.controls.gender;
  }

  public get name(): FormControl<string> {
    return this.deceasedInformationForm.controls.name;
  }

  public get firstname(): FormControl<string> {
    return this.deceasedInformationForm.controls.firstname;
  }

  public get dateOfBirth(): FormControl<Date | undefined> {
    return this.deceasedInformationForm.controls.dateOfBirth;
  }

  public get countryOfBirth(): FormControl<string> {
    return this.deceasedInformationForm.controls.countryOfBirth;
  }

  public get departmentOfBirth(): FormControl<string> {
    return this.deceasedInformationForm.controls.departmentOfBirth;
  }

  public get communeOfBirth(): FormControl<string> {
    return this.deceasedInformationForm.controls.communeOfBirth;
  }

  public get address(): FormControl<string> {
    return this.deceasedInformationForm.controls.address;
  }

  public get zipcode(): FormControl<string> {
    return this.deceasedInformationForm.controls.zipcode;
  }

  public get complementaryAddress(): FormControl<string> {
    return this.deceasedInformationForm.controls.complementaryAddress;
  }

  public get city(): FormControl<string> {
    return this.deceasedInformationForm.controls.city;
  }

  public get country(): FormControl<string> {
    return this.deceasedInformationForm.controls.country;
  }

  public get maritalStatus(): FormControl<string> {
    return this.deceasedInformationForm.controls.maritalStatus;
  }

  public get iban(): FormControl<string> {
    return this.deceasedInformationForm.controls.iban;
  }

  public setDeceasedFormDefaultValues(): void {
    this.successionFacade.customerInformation$
      .pipe(
        filter(
          (customerInformation: CustomerInformation) => !!customerInformation.deceasedInformations,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((customerInformation: CustomerInformation) => {
        this.deceasedInformationForm.reset(customerInformation.deceasedInformations);
      });
  }

  public registerCountriesFieldValidators(): void {
    this.countryOfBirth.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        if (value === Country.FRANCE) {
          this.departmentOfBirth.addValidators(Validators.required);
          this.communeOfBirth.addValidators(Validators.required);
        }

        if (value !== Country.FRANCE) {
          this.departmentOfBirth.removeValidators(Validators.required);
          this.communeOfBirth.removeValidators(Validators.required);
        }

        this.departmentOfBirth.updateValueAndValidity();
        this.communeOfBirth.updateValueAndValidity();
      });

    this.country.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string) => {
        if (value === Country.FRANCE) {
          this.zipcode.addValidators(Validators.required);
          this.city.addValidators(Validators.required);
        }

        if (value !== Country.FRANCE) {
          this.zipcode.removeValidators(Validators.required);
          this.city.removeValidators(Validators.required);
        }

        this.zipcode.updateValueAndValidity();
        this.city.updateValueAndValidity();
      });
  }

  public onNext(): void {
    this.deceasedInformationForm.markAllAsTouched();
    if (this.deceasedInformationForm.invalid) {
      return;
    }

    const deceasedInformation: DeceasedInformation = this.deceasedInformationForm.getRawValue();
    const customerInformation: CustomerInformation = {
      token: this.successionFacade.token,
      savePage: FormStep.ONE,
      loadPage: FormStep.TWO,
      deceasedInformations: deceasedInformation,
      deceasedMoreInformations: undefined,
      reporterInformations: undefined,
      notaryInformations: undefined,
      inheritanceInformations: undefined,
    };

    this.successionFacade.saveAndLoad(customerInformation);
    this.successionFacade.trackNext();
  }
}
